import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import menuIcon from "./img/Expand.svg";
import { useSelector } from "react-redux";
import { RootState } from "@root/store/store";
import logo_cashd from "@root/assets/LoginV2/logo_cashd.svg";
import colorVariables from "@root/configs/colorVariables";
import { ScreenPaths } from "@root/configs/paths";
import { ReactComponent as HomeIcon } from "./img/Home.svg";
import { ReactComponent as TransactionsIcon } from "./img/Transactions.svg";
import { ReactComponent as WithdrawIcon } from "./img/CurrencyCircleDollar.svg";
import { ReactComponent as SupportIcon } from "./img/Support.svg";
import T from "@root/T";

type NavItem = {
  label: string;
  path: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  strokeOnly?: boolean;
};

export const NAV_ITEMS: NavItem[] = [
  { label: T.DASHBOARD, path: ScreenPaths.BALANCE, Icon: HomeIcon },
  {
    label: T.TRANSACTIONS,
    path: ScreenPaths.TRANSACTIONS_PATH,
    Icon: TransactionsIcon,
  },
  {
    label: T.WITHDRAW_MONEY,
    path: ScreenPaths.WITHDRAWAL_PATH,
    Icon: WithdrawIcon,
    strokeOnly: true,
  },
  {
    label: T.SUMMARY,
    path: ScreenPaths.DEDUCTION_PATH,
    Icon: TransactionsIcon,
  },
  { label: T.SUPPORT, path: ScreenPaths.SUPPORT_PATH, Icon: SupportIcon },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export const DashboardSidebar: React.FC<Props> = ({
  collapsed,
  onToggle,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { primaryColor, logoUrl, custom } = useSelector(
    (state: RootState | any) => state.whiteLabellingData
  );

  const primary =
    primaryColor || colorVariables.defaultPrimarycolor;

  const isActive = (path: string) =>
    location.pathname === path ||
    location.pathname.startsWith(path + "/");

  return (
    <div
      className="flex flex-col text-white transition-all duration-300 h-full"
      style={{
        width: collapsed ? 90 : 267,
        backgroundColor: primary,
      }}
    >
      {/* Logo + Toggle */}
      <div
        className={`flex items-center ${collapsed ? "justify-center" : "justify-between"
          } px-4 py-6 `}
      >
        {!collapsed && (
          <>
            {custom ? (
              <>
                {logoUrl ? (
                  <img
                    className="img-fluid cursor-pointer max-w-[172px]"
                    src={logoUrl}
                    alt="logo"
                    onClick={() => navigate("/")}
                  />
                ) : (
                  <img
                    className="img-fluid cursor-pointer max-w-[172px]"
                    src={logo_cashd}
                    alt="logo"
                    onClick={() => navigate("/")}
                  />
                )}
              </>
            ) : (
              <img
                className="img-fluid cursor-pointer max-w-[172px]"
                src={logo_cashd}
                alt="logo"
                onClick={() => navigate("/")}
              />
            )}
          </>
        )}

        <button
          onClick={onToggle}
          className="text-white hover:opacity-80 transition"
        >
          <img
            src={menuIcon}
            alt="Menu"
            className={`transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Divider under logo */}
      {!collapsed && (
        <div
          className={`border-t border-white/30 ${collapsed ? "mx-3" : "mx-6"}`}
        />
      )}

      {/* Navigation */}
      <div className="flex flex-col gap-2 px-3 mt-3">
        {NAV_ITEMS.map(({ label, path, Icon, strokeOnly }) => {
          const active = isActive(path);
          const itemColor = active ? primary : "#FFFFFF";
          const iconClassName = strokeOnly
            ? "[&_path]:stroke-current [&_path]:fill-none"
            : "[&_path]:fill-current [&_path]:stroke-none";

          return (
            <NavLink key={path} to={path}>
              <div
                title={collapsed ? label : ""}
                className={`flex items-center rounded-lg py-3 text-base font-normal transition-all duration-200
                  ${collapsed ? "justify-center px-0" : "gap-3 px-4"}
                  ${active ? "bg-white" : "hover:bg-white/10"}
                `}
                style={{ color: itemColor }}
              >
                <Icon
                  width={collapsed ? 28 : 22}
                  height={collapsed ? 28 : 22}
                  className={`
                    ${iconClassName}
                  `}
                />
                {!collapsed && <span>{label}</span>}
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};